/**
 * Voting check-in (rule CHECKIN), for ushers on their own phone: right before the vote, scan the
 * sticker QR of each minister to enable them. Closes by itself when the first round opens.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, Lock, ScanLine, Search, Undo2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QrScanner } from '@/features/events/checkin/components/QrScanner';
import { cn } from '@/lib/utils';
import { checkinVoter, searchCheckin, undoCheckin } from '../data/votingRepository';
import { CheckinCounter } from '../components/CheckinCounter';
import { useCheckinStats, votingKeys } from '../hooks/useVoting';
import { MinisterPhoto } from '../components/MinisterPhoto';
import type { CheckinResult, CheckinSearchRow } from '../types';
import { KIND_BADGE, REASON_LABEL } from '../utils/labels';
import { badgeTokenFromScan } from '../utils/publicUrl';
import { votingErrorMessage } from '../utils/votingErrors';

const TONE = {
  ok: 'border-green-600 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100',
  warn: 'border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100',
  bad: 'border-red-600 bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100',
} as const;

function describe(r: CheckinResult): { tone: keyof typeof TONE; title: string; detail?: string } {
  switch (r.result) {
    case 'OK':
      return { tone: 'ok', title: 'Habilitado para votar' };
    case 'ALREADY':
      return { tone: 'warn', title: 'Ya estaba habilitado', detail: `Registrado a las ${new Date(r.checked_in_at).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}` };
    case 'NOT_ELIGIBLE':
      return { tone: 'bad', title: 'No puede votar', detail: REASON_LABEL[r.reason] ?? r.reason };
    case 'CLOSED':
      return { tone: 'bad', title: 'El registro está cerrado', detail: 'La votación ya empezó.' };
    case 'WRONG_EVENT':
      return { tone: 'bad', title: 'Credencial de otro evento' };
    case 'RULE_NOT_CHECKIN':
      return { tone: 'bad', title: 'Esta elección no usa registro previo' };
    case 'NOT_FOUND':
      return { tone: 'bad', title: 'Credencial no reconocida' };
  }
}

export function CheckinPage() {
  const { electionId = '' } = useParams<{ electionId: string }>();
  const qc = useQueryClient();
  const { data: stats, error: statsError } = useCheckinStats(electionId);
  const [scanning, setScanning] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CheckinResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<CheckinSearchRow[] | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: votingKeys.checkinStats(electionId) });

  const run = async (who: { badgeToken: string } | { participantId: string }) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await checkinVoter(electionId, who);
      setResult(r);
      setScanning(false);
      refresh();
    } catch (err) {
      setError(votingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const undo = async (participantId: string) => {
    setBusy(true);
    try {
      await undoCheckin(electionId, participantId);
      setResult(null);
      setScanning(true);
      refresh();
    } catch (err) {
      setError(votingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const search = async () => {
    setBusy(true);
    setError(null);
    try {
      setRows(await searchCheckin(electionId, query.trim()));
    } catch (err) {
      setError(votingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (statsError) return <p className="p-6 text-destructive">{votingErrorMessage(statsError)}</p>;
  if (!stats) return <div className="flex justify-center py-16"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;

  const closed = stats.started;
  const wrongRule = stats.rule !== 'CHECKIN';
  const info = result ? describe(result) : null;
  const person = result && 'person' in result ? result.person : null;

  return (
    <div className="mx-auto max-w-lg space-y-4 p-4">
      <header className="space-y-1">
        <h1 className="text-xl font-bold">Habilitar votantes</h1>
        <p className="text-sm text-muted-foreground">{stats.election_name}</p>
        <CheckinCounter electionId={electionId} className="font-semibold" />
      </header>

      {wrongRule && (
        <p className="flex items-center gap-2 rounded-md border border-amber-500 p-3 text-sm"><AlertTriangle className="size-4" /> Esta elección no usa registro previo.</p>
      )}
      {closed && (
        <p className="flex items-center gap-2 rounded-md border border-red-600 p-3 text-sm"><Lock className="size-4" /> La votación ya empezó: el registro está cerrado.</p>
      )}
      {error && <p className="rounded-md border border-red-600 p-3 text-sm text-red-700">{error}</p>}

      {!wrongRule && !closed && scanning && (
        <div className="space-y-2">
          <p className="text-center text-sm text-muted-foreground">Apunte la cámara al QR del sticker</p>
          <div className="overflow-hidden rounded-lg">
            <QrScanner
              paused={busy}
              onScan={(text) => {
                const t = badgeTokenFromScan(text);
                if (t) void run({ badgeToken: t });
              }}
            />
          </div>
        </div>
      )}

      {info && (
        <div className={cn('space-y-3 rounded-xl border-2 p-4', TONE[info.tone])}>
          <div className="flex items-center gap-2 text-xl font-bold">
            {info.tone === 'ok' ? <CheckCircle2 className="size-6" /> : info.tone === 'warn' ? <AlertTriangle className="size-6" /> : <XCircle className="size-6" />}
            {info.title}
          </div>
          {person && (
            <div className="flex items-center gap-3">
              <MinisterPhoto filename={person.photo_filename} name={person.full_name} className="size-20" />
              <div>
                <p className="text-lg font-semibold">{person.full_name}</p>
                <p className="text-sm opacity-80">{KIND_BADGE[person.kind]} · CI {person.ci ?? '—'}</p>
                {person.district_name && <p className="text-sm opacity-80">{person.district_name}</p>}
              </div>
            </div>
          )}
          {info.detail && <p>{info.detail}</p>}
          <div className="flex flex-wrap gap-2">
            <Button className="h-12 flex-1" onClick={() => { setResult(null); setScanning(true); }} disabled={closed}>
              <ScanLine className="mr-2 size-5" /> Siguiente
            </Button>
            {person && result && (result.result === 'OK' || result.result === 'ALREADY') && !closed && (
              <Button variant="outline" className="h-12" disabled={busy} onClick={() => void undo(person.participant_id)}>
                <Undo2 className="mr-2 size-4" /> Deshacer
              </Button>
            )}
          </div>
        </div>
      )}

      {!wrongRule && !closed && (
        <section className="space-y-2 border-t pt-4">
          <p className="text-sm font-medium">¿No lee el QR? Busque por CI, código ADB o nombre</p>
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); void search(); }}>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="CI, ADB-… o nombre" inputMode="text" autoComplete="off" />
            <Button type="submit" disabled={busy || query.trim().length < 3}><Search className="size-4" /></Button>
          </form>
          {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">Sin resultados.</p>}
          {rows && rows.length > 0 && (
            <ul className="divide-y rounded-md border">
              {rows.map((r) => (
                <li key={r.participant_id} className="flex items-center justify-between gap-2 p-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{r.full_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {KIND_BADGE[r.kind]} · CI {r.ci ?? '—'}
                      {r.reason ? ` · ${REASON_LABEL[r.reason] ?? r.reason}` : ''}
                    </p>
                  </div>
                  {r.checked_in_at ? (
                    <span className="text-xs text-green-700">Habilitado</span>
                  ) : (
                    <Button size="sm" disabled={busy || !!r.reason} onClick={() => void run({ participantId: r.participant_id })}>Habilitar</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
